import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import { useRegionContext } from '../../region/RegionContext';
import { queryAllRegions } from '../../utils/queryAllRegions';
import { getRegions } from '../../region/regionManager';
import TabDescription from './TabDescription';
import 'leaflet/dist/leaflet.css';

// Approximate geographic centers for each country (lat, lon)
const COUNTRY_CENTERS = {
  AU: { lat: -25.2744, lon: 133.7751 },
  NZ: { lat: -41.2865, lon: 174.7762 },
  FJ: { lat: -17.7134, lon: 178.0650 },
  CA: { lat: 56.1304, lon: -106.3468 },
  US: { lat: 37.0902, lon: -95.7129 },
  UK: { lat: 55.3781, lon: -3.4360 },
  IN: { lat: 20.5937, lon: 78.9629 },
  CN: { lat: 35.8617, lon: 104.1954 },
  IE: { lat: 53.1424, lon: -7.6921 },
  SG: { lat: 1.3521, lon: 103.8198 },
  JP: { lat: 36.2048, lon: 138.2529 },
  KR: { lat: 35.9078, lon: 127.7669 },
  DE: { lat: 51.1657, lon: 10.4515 },
  FR: { lat: 46.2276, lon: 2.2137 },
  BR: { lat: -14.2350, lon: -51.9253 },
  ZA: { lat: -30.5595, lon: 22.9375 },
  AE: { lat: 23.4241, lon: 53.8478 },
  TH: { lat: 15.8700, lon: 100.9925 },
  MY: { lat: 4.2105, lon: 101.9758 },
  ID: { lat: -0.7893, lon: 113.9213 },
  PH: { lat: 12.8797, lon: 121.7740 },
  MX: { lat: 23.6345, lon: -102.5528 },
  IT: { lat: 41.8719, lon: 12.5674 },
  ES: { lat: 40.4637, lon: -3.7492 },
};

const VenueMap = () => {
  const { db, isGlobal, allRegionDbs, activeRegion } = useRegionContext();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groupBy, setGroupBy] = useState(isGlobal ? 'country' : 'suburb'); // suburb, state, country
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [regionData, setRegionData] = useState([]);
  const [venueFilter, setVenueFilter] = useState('all');

  useEffect(() => {
    if (db) loadVenues();
  }, [db, isGlobal, allRegionDbs]);

  const filteredVenues = venues.filter((v) => {
    if (venueFilter === 'claimed') return v.claimStatus === 'claimed';
    if (venueFilter === 'partner') return !!v.bizStripeId;
    return true;
  });

  useEffect(() => {
    if (filteredVenues.length > 0) {
      processRegionData();
    } else {
      setRegionData([]);
    }
  }, [filteredVenues.length, venues, groupBy, venueFilter]);

  const loadVenues = async () => {
    try {
      setLoading(true);

      let data;
      const buildQuery = (ref) => ref.collection('Business').limit(isGlobal ? 2000 : 5000);

      if (isGlobal && allRegionDbs) {
        data = await queryAllRegions(allRegionDbs, null, buildQuery);
      } else {
        const snapshot = await buildQuery(db).get();
        data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
      }

      setVenues(data);
    } catch (err) {
      console.error('Error loading venues:', err);
      setError('Failed to load venue data');
    } finally {
      setLoading(false);
    }
  };

  const regions = getRegions();

  const processRegionData = () => {
    const regionCounts = {};
    const regionCoords = {};

    filteredVenues.forEach(venue => {
      let region;
      let lat = venue.bizLat || venue.latitude;
      let lon = venue.bizLon || venue.longitude;

      if (groupBy === 'country') {
        // Use _region (country code from queryAllRegions) for reliable grouping
        const code = venue._region || 'AU';
        region = regions[code]?.country || code;
        // Use known country center if venue has no coords
        if (!lat || !lon) {
          const center = COUNTRY_CENTERS[code] || COUNTRY_CENTERS['AU'];
          lat = center.lat;
          lon = center.lon;
        }
      } else if (groupBy === 'state') {
        region = venue.bizState || venue.importState || 'Unknown';
        // State centers (approximate)
        const stateCenters = {
          'VIC': { lat: -37.4713, lon: 144.7852 },
          'NSW': { lat: -33.8688, lon: 151.2093 },
          'QLD': { lat: -27.4698, lon: 153.0251 },
          'SA': { lat: -34.9285, lon: 138.6007 },
          'WA': { lat: -31.9505, lon: 115.8605 },
          'TAS': { lat: -42.8821, lon: 147.3272 },
          'ACT': { lat: -35.2809, lon: 149.1300 },
          'NT': { lat: -12.4634, lon: 130.8456 },
        };
        if (!lat || !lon) {
          const center = stateCenters[region] || stateCenters['VIC'];
          lat = center.lat;
          lon = center.lon;
        }
      } else if (groupBy === 'suburb') {
        region = venue.bizSuburb || venue.importCity || 'Unknown';
      }

      if (!region) return;

      // Count venues per region
      if (!regionCounts[region]) {
        regionCounts[region] = 0;
        regionCoords[region] = { lat: [], lon: [] };
      }
      regionCounts[region]++;

      // Collect coordinates for averaging
      if (lat && lon) {
        regionCoords[region].lat.push(lat);
        regionCoords[region].lon.push(lon);
      }
    });

    // Calculate average coordinates and prepare data
    const processed = Object.entries(regionCounts).map(([name, count]) => {
      const coords = regionCoords[name];
      const avgLat = coords.lat.length > 0
        ? coords.lat.reduce((a, b) => a + b, 0) / coords.lat.length
        : -37.8136; // Default Melbourne
      const avgLon = coords.lon.length > 0
        ? coords.lon.reduce((a, b) => a + b, 0) / coords.lon.length
        : 144.9631;

      return {
        name,
        count,
        lat: avgLat,
        lon: avgLon,
      };
    });

    // Sort by count descending
    processed.sort((a, b) => b.count - a.count);

    setRegionData(processed);
  };

  const getColor = (count, maxCount) => {
    const intensity = count / maxCount;

    if (intensity > 0.7) return '#B91C1C'; // Dark red
    if (intensity > 0.5) return '#DC2626'; // Red
    if (intensity > 0.3) return '#EF4444'; // Light red
    if (intensity > 0.1) return '#F87171'; // Lighter red
    return '#FCA5A5'; // Very light red
  };

  const getRadius = (count, maxCount) => {
    const baseRadius = 5;
    const maxRadius = 50;
    const scaledRadius = (count / maxCount) * maxRadius;
    return Math.max(baseRadius, Math.min(maxRadius, scaledRadius)) * 1000; // Convert to meters
  };

  const maxCount = regionData.length > 0 ? Math.max(...regionData.map(r => r.count)) : 1;

  const MapUpdater = ({ center, zoom }) => {
    const map = useMap();
    useEffect(() => {
      map.setView(center, zoom);
    }, [center, zoom, map]);
    return null;
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading venue map...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  // Center map on the selected country tab, or world view for Global
  const regionCenter = COUNTRY_CENTERS[activeRegion] || COUNTRY_CENTERS['AU'];
  const defaultCenter = isGlobal ? [20, 0] : [regionCenter.lat, regionCenter.lon];
  const defaultZoom = isGlobal
    ? 2
    : groupBy === 'country' ? 4 : groupBy === 'state' ? 5 : 10;

  return (
    <Box>
      <TabDescription>
        Geographic heat map of venues, plotted using bizLat/bizLon coordinates.
        Circle size and colour intensity indicate venue density per region. Group by country, state, or suburb
        to identify coverage gaps and high-density areas. Use the filter to show all venues, only claimed
        venues, or only Piatto Partners (venues with a connected Stripe account).
      </TabDescription>
      {/* Controls */}
      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth>
            <InputLabel>Group By</InputLabel>
            <Select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              label="Group By"
            >
              <MenuItem value="country">Country</MenuItem>
              <MenuItem value="state">State</MenuItem>
              <MenuItem value="suburb">Suburb</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth>
            <InputLabel>Filter</InputLabel>
            <Select
              value={venueFilter}
              onChange={(e) => setVenueFilter(e.target.value)}
              label="Filter"
            >
              <MenuItem value="all">All Venues</MenuItem>
              <MenuItem value="claimed">Claimed Only</MenuItem>
              <MenuItem value="partner">Piatto Partner</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4} md={6}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={`${
                venueFilter === 'claimed' ? 'Claimed' : venueFilter === 'partner' ? 'Partner' : 'Total'
              } Venues: ${filteredVenues.length}`}
              color="primary"
            />
            <Chip label={`Regions: ${regionData.length}`} />
            {regionData.length > 0 && (
              <Chip
                label={`Top: ${regionData[0].name} (${regionData[0].count})`}
                color="success"
              />
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Map */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box sx={{ height: 600, width: '100%' }}>
            <MapContainer
              center={defaultCenter}
              zoom={defaultZoom}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              <MapUpdater center={defaultCenter} zoom={defaultZoom} />

              {regionData.map((region) => (
                <CircleMarker
                  key={region.name}
                  center={[region.lat, region.lon]}
                  radius={Math.min(Math.log(region.count + 1) * 5, 50)} // Log scale, capped at 50px
                  pathOptions={{
                    color: getColor(region.count, maxCount),
                    fillColor: getColor(region.count, maxCount),
                    fillOpacity: 0.6,
                    weight: 2,
                  }}
                  eventHandlers={{
                    click: () => setSelectedRegion(region),
                  }}
                >
                  <Tooltip permanent={region.count > maxCount * 0.3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {region.name}
                      </Typography>
                      <Typography variant="body2">
                        {region.count} venue{region.count !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Tooltip>
                </CircleMarker>
              ))}
            </MapContainer>
          </Box>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Heat Map Legend
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  bgcolor: '#FCA5A5',
                }}
              />
              <Typography variant="body2">1-{Math.floor(maxCount * 0.1)} venues</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  bgcolor: '#F87171',
                }}
              />
              <Typography variant="body2">{Math.floor(maxCount * 0.1)}-{Math.floor(maxCount * 0.3)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  bgcolor: '#EF4444',
                }}
              />
              <Typography variant="body2">{Math.floor(maxCount * 0.3)}-{Math.floor(maxCount * 0.5)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  bgcolor: '#DC2626',
                }}
              />
              <Typography variant="body2">{Math.floor(maxCount * 0.5)}-{Math.floor(maxCount * 0.7)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  bgcolor: '#B91C1C',
                }}
              />
              <Typography variant="body2">{Math.floor(maxCount * 0.7)}+ venues (Highest)</Typography>
            </Box>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            💡 Tip: Click on any circle to see details. Darker colors and larger circles indicate more venues in that region.
          </Typography>
        </CardContent>
      </Card>

      {/* Top Regions */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Top 10 Regions
          </Typography>
          <Grid container spacing={2}>
            {regionData.slice(0, 10).map((region, index) => (
              <Grid item xs={12} sm={6} md={4} key={region.name}>
                <Box
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    bgcolor: selectedRegion?.name === region.name ? 'grey.100' : 'white',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'grey.50' },
                  }}
                  onClick={() => setSelectedRegion(region)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {index + 1}. {region.name}
                    </Typography>
                    <Chip
                      label={region.count}
                      size="small"
                      sx={{
                        bgcolor: getColor(region.count, maxCount),
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VenueMap;
